import { useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/select';
import { useTranslation } from 'react-i18next';
import { Plus, Building2, Smartphone, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAddPayoutMethod } from '../hooks/use-payout-methods';
import { payoutService } from '../services/payout.service';
import type {
  CreatePayoutMethodData,
  ManualPayoutMethod,
  PayoutMethodType,
} from '../types/payout.types';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/molecules/dialog';

const MOBILE_PROVIDERS = [
  { value: 'ORANGE_MONEY', label: 'Orange Money' },
  { value: 'MPESA', label: 'M-Pesa' },
  { value: 'MTN_MOMO', label: 'MTN Mobile Money' },
  { value: 'WAVE', label: 'Wave' },
  { value: 'FREE_MONEY', label: 'Free Money' },
];

const MOBILE_COUNTRIES = [
  { value: 'KM', label: 'Comores' },
  { value: 'SN', label: 'Senegal' },
  { value: 'CI', label: "Cote d'Ivoire" },
  { value: 'ML', label: 'Mali' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'CM', label: 'Cameroun' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'KE', label: 'Kenya' },
];

interface AddPayoutMethodFormProps {
  /** Ouverture contrôlée (ex. déclenchée par l'onboarding DESTINATION_REQUIRED). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Masque le bouton déclencheur intégré (quand l'ouverture est pilotée dehors). */
  hideTrigger?: boolean;
  /**
   * Doc 51 Phase 5 — méthodes manuelles disponibles pour ce Merchant (issues de
   * `eligible-psps`). Défaut : les deux (rétro-compat). Un onglet n'est affiché
   * que si sa méthode est disponible.
   */
  methods?: ManualPayoutMethod[];
}

const METHOD_TO_TYPE: Record<ManualPayoutMethod, PayoutMethodType> = {
  IBAN: 'BANK_TRANSFER',
  MOBILE_MONEY: 'MOBILE_MONEY',
};

export function AddPayoutMethodForm({
  open: controlledOpen,
  onOpenChange,
  hideTrigger,
  methods,
}: AddPayoutMethodFormProps = {}) {
  const { t } = useTranslation();
  const addMethod = useAddPayoutMethod();
  const [internalOpen, setInternalOpen] = useState(false);
  // Contrôlé si `open`/`onOpenChange` fournis, sinon état interne (rétro-compat).
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  // Types de destination réellement disponibles (défaut : les deux).
  const availableMethods: ManualPayoutMethod[] =
    methods && methods.length > 0 ? methods : ['IBAN', 'MOBILE_MONEY'];
  const availableTypes = availableMethods.map((m) => METHOD_TO_TYPE[m]);
  const showBank = availableTypes.includes('BANK_TRANSFER');
  const showMobile = availableTypes.includes('MOBILE_MONEY');
  const [type, setType] = useState<PayoutMethodType>(availableTypes[0]);

  // Bank fields
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');

  // Mobile Money fields
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileProvider, setMobileProvider] = useState('');
  const [mobileCountry, setMobileCountry] = useState('');

  // Doc 51 Phase 5 — étape MFA (email OTP) avant l'ajout effectif.
  const [step, setStep] = useState<'details' | 'mfa'>('details');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [pendingData, setPendingData] = useState<CreatePayoutMethodData | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);

  const resetForm = () => {
    setType(availableTypes[0]);
    setBankName('');
    setAccountName('');
    setIban('');
    setBic('');
    setMobileNumber('');
    setMobileProvider('');
    setMobileCountry('');
    setStep('details');
    setCode('');
    setChallengeId(null);
    setPendingData(null);
  };

  const buildData = (): CreatePayoutMethodData =>
    type === 'BANK_TRANSFER'
      ? {
          type,
          bankName,
          accountName,
          iban: iban.replace(/\s/g, ''),
          bic: bic.replace(/\s/g, ''),
          isDefault: false,
        }
      : {
          type,
          mobileNumber,
          mobileProvider,
          mobileCountry,
          isDefault: false,
        };

  // Étape 1 → demande d'un code MFA par email, puis passage à l'étape OTP.
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaLoading(true);
    try {
      const data = buildData();
      const { challengeId: id } = await payoutService.requestMfaChallenge('PAYOUT_DESTINATION');
      setChallengeId(id);
      setPendingData(data);
      setStep('mfa');
      toast.success(t('payouts.mfa.codeSent', 'Un code de vérification vous a été envoyé par email.'));
    } catch (error: any) {
      toast.error(error?.message || t('payouts.methods.addError'));
    } finally {
      setMfaLoading(false);
    }
  };

  // Étape 2 → vérification du code, puis ajout effectif (le serveur applique
  // l'attestation MFA → destination APPROVED).
  const handleVerifyAndAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId || !pendingData) return;
    setMfaLoading(true);
    try {
      await payoutService.verifyMfaChallenge(challengeId, code.trim());
      await addMethod.mutateAsync(pendingData);
      toast.success(t('payouts.methods.addSuccess'));
      resetForm();
      setOpen(false);
    } catch (error: any) {
      toast.error(error?.message || t('payouts.mfa.invalidCode', 'Code invalide ou expiré.'));
    } finally {
      setMfaLoading(false);
    }
  };

  const submitting = mfaLoading || addMethod.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetForm();
        setOpen(o);
      }}
    >
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full border-dashed">
            <Plus className="size-4 mr-2" />
            {t('payouts.methods.add')}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-md">
        {step === 'details' ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('payouts.methods.addTitle')}</DialogTitle>
              <DialogDescription>{t('payouts.methods.addDescription')}</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleContinue} className="space-y-6">
              {/* Sélecteur de méthode — n'apparaît que si les DEUX sont disponibles
                  (sinon la seule méthode disponible est déjà sélectionnée). */}
              {showBank && showMobile && (
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setType('BANK_TRANSFER')}
                    className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      type === 'BANK_TRANSFER' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                    }`}
                  >
                    <Building2 className="size-8 mb-2 text-blue-600" />
                    <span className="font-medium">{t('payouts.methods.bankTransfer')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('MOBILE_MONEY')}
                    className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-colors ${
                      type === 'MOBILE_MONEY' ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                    }`}
                  >
                    <Smartphone className="size-8 mb-2 text-green-600" />
                    <span className="font-medium">{t('payouts.methods.mobileMoney')}</span>
                  </button>
                </div>
              )}

              {type === 'BANK_TRANSFER' ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankName">{t('payouts.methods.bankName')}</Label>
                    <Input
                      id="bankName"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      placeholder="BNP Paribas"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountName">{t('payouts.methods.accountName')}</Label>
                    <Input
                      id="accountName"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN</Label>
                    <Input
                      id="iban"
                      value={iban}
                      onChange={(e) => setIban(e.target.value.toUpperCase())}
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bic">BIC/SWIFT</Label>
                    <Input
                      id="bic"
                      value={bic}
                      onChange={(e) => setBic(e.target.value.toUpperCase())}
                      placeholder="BNPAFRPP"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="mobileCountry">{t('payouts.methods.country')}</Label>
                    <Select value={mobileCountry} onValueChange={setMobileCountry} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('payouts.methods.selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {MOBILE_COUNTRIES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobileProvider">{t('payouts.methods.provider')}</Label>
                    <Select value={mobileProvider} onValueChange={setMobileProvider} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('payouts.methods.selectProvider')} />
                      </SelectTrigger>
                      <SelectContent>
                        {MOBILE_PROVIDERS.map((provider) => (
                          <SelectItem key={provider.value} value={provider.value}>
                            {provider.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobileNumber">{t('payouts.methods.phoneNumber')}</Label>
                    <Input
                      id="mobileNumber"
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      placeholder="+269 321 23 45"
                      required
                    />
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <ShieldCheck className="size-4 mr-2" />
                    {t('payouts.mfa.continue', 'Continuer (vérification par code)')}
                  </>
                )}
              </Button>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('payouts.mfa.title', 'Vérification de sécurité')}</DialogTitle>
              <DialogDescription>
                {t(
                  'payouts.mfa.description',
                  'Saisissez le code à 6 chiffres envoyé par email pour confirmer votre destination de versement.'
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleVerifyAndAdd} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="mfaCode">{t('payouts.mfa.code', 'Code de vérification')}</Label>
                <Input
                  id="mfaCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  required
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep('details');
                    setCode('');
                  }}
                  disabled={submitting}
                >
                  <ArrowLeft className="size-4 mr-2" />
                  {t('common.back', 'Retour')}
                </Button>
                <Button type="submit" className="flex-1" disabled={submitting || code.length < 6}>
                  {submitting ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="size-4 mr-2" />
                      {t('payouts.mfa.verifyAndAdd', 'Vérifier et ajouter')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
